import {
  Combobox as ComboboxPrimitive,
  ComboboxChipProps as ComboboxPrimitiveChipProps,
  ComboboxChipsProps,
  ComboboxEmptyProps,
  ComboboxInputProps,
  ComboboxItemProps,
  ComboboxListProps,
  ComboboxPopupProps,
  ComboboxPositionerProps,
  type ComboboxRootChangeEventDetails,
  ComboboxRootProps,
  ComboboxTriggerProps as ComboboxPrimitiveTriggerProps,
  ComboboxValueProps,
} from "@base-ui/react/combobox"
import { useRef } from "react"

import Icon from "#React/ui/Icon/Icon"
import cn from "#Utils/cn"

const ComboboxRoot = ComboboxPrimitive.Root

function ComboboxValue(props: ComboboxValueProps) {
  return <ComboboxPrimitive.Value {...props} />
}

type ComboboxTriggerProps = {
  placeholder?: string
} & ComboboxPrimitiveTriggerProps

function ComboboxTrigger({ className, placeholder, render, ...props }: ComboboxTriggerProps) {
  return (
    <ComboboxPrimitive.Trigger
      className={className}
      render={
        render || (
          <button className="c-button c-button--white rounded-md w-full flex disabled:opacity-50 disabled:pointer-events-none">
            <span className="flex-1 text-left overflow-y-hidden text-ellipsis">
              <ComboboxValue placeholder={placeholder} />
            </span>
            <Icon variant="disclosure" tone="muted" />
          </button>
        )
      }
      {...props}
    />
  )
}

type ComboboxSearchProps = ComboboxInputProps

function ComboboxSearch({ className, ...props }: ComboboxSearchProps) {
  return (
    <div role="group" className="flex w-full p-1 pb-0">
      <ComboboxPrimitive.Input className={cn("c-formv2__input min-w-0", className)} {...props} />
    </div>
  )
}

type ComboboxContentProps = Pick<
  ComboboxPositionerProps,
  "side" | "sideOffset" | "align" | "alignOffset" | "anchor"
> &
  ComboboxPopupProps

function ComboboxContent({
  className,
  side = "bottom",
  sideOffset = 6,
  align = "start",
  alignOffset = 0,
  anchor,
  ...props
}: ComboboxContentProps) {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="z-1000"
      >
        <ComboboxPrimitive.Popup
          className={cn(
            "bg-surface-default border border-edge-default max-h-96 overflow-hidden rounded-md shadow-md group/combobox-content relative min-w-64 max-w-(--available-width)",
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  )
}

function ComboboxList({ className, ...props }: ComboboxListProps) {
  return (
    <ComboboxPrimitive.List
      className={cn(
        "no-scrollbar max-h-[min(calc(--spacing(96)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1 overflow-y-auto p-1 data-empty:p-0 overscroll-contain",
        className,
      )}
      {...props}
    />
  )
}

type ComboboxItemExtendedProps = {
  showIndicator?: boolean
} & ComboboxItemProps

function ComboboxItem({
  className,
  children,
  showIndicator = true,
  ...props
}: ComboboxItemExtendedProps) {
  return (
    <ComboboxPrimitive.Item
      className={cn(
        "data-highlighted:bg-surface-raised data-selected:font-medium gap-2 rounded-sm py-2 lg:py-1 pl-1.5 text-sm relative flex w-full cursor-pointer items-center outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50",
        showIndicator ? "pr-8" : "pr-1.5",
        className,
      )}
      {...props}
    >
      {children}
      {showIndicator && (
        <ComboboxPrimitive.ItemIndicator
          render={
            <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
              <Icon name="far fa-check fa-fw" />
            </span>
          }
        />
      )}
    </ComboboxPrimitive.Item>
  )
}

function ComboboxEmpty({ className, ...props }: ComboboxEmptyProps) {
  return (
    <ComboboxPrimitive.Empty
      className={cn(
        "text-gray-500 hidden w-full justify-center py-2 text-center text-sm group-data-empty/combobox-content:flex",
        className,
      )}
      {...props}
    />
  )
}

type ComboboxSeparatorProps = ComboboxPrimitive.Separator.Props

function ComboboxSeparator({ className, ...props }: ComboboxSeparatorProps) {
  return (
    <ComboboxPrimitive.Separator
      className={cn("bg-edge-default -mx-1 my-1 h-px", className)}
      {...props}
    />
  )
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
  return <ComboboxPrimitive.Group className={cn(className)} {...props} />
}

function ComboboxLabel({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) {
  return (
    <ComboboxPrimitive.GroupLabel
      className={cn("px-1.5 py-1.5 text-xs font-semibold", className)}
      {...props}
    />
  )
}

function ComboboxCollection({ ...props }: ComboboxPrimitive.Collection.Props) {
  return <ComboboxPrimitive.Collection {...props} />
}

function ComboboxChips({ className, ...props }: ComboboxChipsProps) {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      className={cn(
        "c-formv2__input c-formv2__textfield min-h-8 h-auto flex-wrap items-center gap-1 py-1 has-data-[slot=combobox-chip]:px-1",
        className,
      )}
      {...props}
    />
  )
}

type ComboboxChipProps = {
  showRemove?: boolean
} & ComboboxPrimitiveChipProps

function ComboboxChip({ className, children, showRemove = true, ...props }: ComboboxChipProps) {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={cn(
        "bg-surface-raised flex h-[calc(--spacing(5.25))] w-fit items-center justify-center gap-1 rounded-xs px-1.5 text-xs font-medium whitespace-nowrap has-data-[slot=combobox-chip-remove]:pr-0 has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          className="-ml-1 opacity-50 hover:opacity-100"
          data-slot="combobox-chip-remove"
          render={
            <button className="p-1 cursor-pointer">
              <Icon name="far fa-close fa-fw" className="pointer-events-none" size="xs" />
            </button>
          }
        />
      )}
    </ComboboxPrimitive.Chip>
  )
}

type ComboboxChipsInputProps = ComboboxInputProps

function ComboboxChipsInput({ className, ...props }: ComboboxChipsInputProps) {
  return (
    <ComboboxPrimitive.Input
      className={cn("c-reset min-w-16 flex-1 outline-none", className)}
      {...props}
    />
  )
}

type ComboboxProps<T, Multiple extends boolean | undefined = false> = {
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  buttonRender?: ComboboxTriggerProps["render"]
} & ComboboxRootProps<T, Multiple>

function Combobox<T, Multiple extends boolean | undefined = false>({
  multiple,
  disabled,
  placeholder = "Select",
  searchPlaceholder = "Search",
  emptyMessage = "No items found.",
  buttonRender,
  ...props
}: ComboboxProps<T, Multiple>) {
  const anchorRef = useRef<HTMLDivElement>(null)
  return (
    <ComboboxRoot<T, Multiple> multiple={multiple} disabled={disabled} autoHighlight {...props}>
      <ComboboxTrigger disabled={disabled} placeholder={placeholder} render={buttonRender} />
      <ComboboxContent anchor={anchorRef.current}>
        <ComboboxSearch placeholder={searchPlaceholder} />
        <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
        <ComboboxList>
          {(item: T) => (
            <ComboboxItem
              key={props.itemToStringValue ? props.itemToStringValue(item) : String(item)}
              value={item}
            >
              {props.itemToStringLabel ? props.itemToStringLabel(item) : String(item)}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  )
}

export {
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxRoot,
  ComboboxSearch,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
}

export type {
  ComboboxRootChangeEventDetails as ComboboxChangeEventDetails,
  ComboboxChipProps,
  ComboboxChipsInputProps,
  ComboboxChipsProps,
  ComboboxContentProps,
  ComboboxEmptyProps,
  ComboboxItemExtendedProps as ComboboxItemProps,
  ComboboxListProps,
  ComboboxProps,
  ComboboxSearchProps,
  ComboboxSeparatorProps,
  ComboboxTriggerProps,
}

export default Combobox
